/**
 * @file ShowDetails.tsx
 * @description Core utilities and calcaultions for the Show Details section of the application
 *
 * This module handles:
 * - Show Details accordion generation and calculations
 * - Various computations for different show values
 * - Deal type mappings (FLAT_FEE, REV_SPLIT, VERSUS) for accordions
 */

import { useMemo } from "react";
import { Show, ShowDealType } from "@quantizr-engineering/common/types/show";
import {
  calculateShowFigures,
  calculateTicketFeeActual,
  ShowFiguresType,
  calculateGrossAdjustments,
  calculateNetAdjustments,
  calculateExpensesProjected,
  calculateExpensesActual,
  calculateGrossDeductions,
  calculateNetDeductions,
  calculateGrossTicketActual,
  calculateGrossTicketProjected,
  calculateTicketFeeProjected,
} from "@quantizr-engineering/common/math/live";

export const SHOW_DETAILS_MAPPING = {
  [ShowDealType.FLAT_FEE]: ["Walkout"],
  [ShowDealType.REV_SPLIT]: ["Walkout", "Revenue To Split"],
  [ShowDealType.VERSUS]: ["Walkout", "Revenue To Split"],
} as const;

export const filterAccordionsByDealType = (
  accordions: any[],
  dealType: ShowDealType
) => {
  const allowedSections =
    SHOW_DETAILS_MAPPING[dealType] ||
    Object.values(SHOW_DETAILS_MAPPING).flat();

  return accordions.filter((accordion) =>
    allowedSections.includes(accordion.headerTitle)
  );
};

export const useShowDetailsCalculations = (values: Show) => {
  const showValues = useMemo<ShowFiguresType>(() => {
    if (!values) {
      return {
        ticketRevenuePotential: 0,
        ticketRevenueProjected: 0,
        sellableCap: 0,
        ticketsSoldActual: 0,
        venueExpensesProjected: 0,
        venueExpenses: 0,
        profitSharePercentPotential: 0,
        profitSharePercentProjected: 0,
        profitSharePercentActual: 0,
        variableRevPotential: 0,
        variableRevActual: 0,
        showNetActual: 0,
        showBuyouts: 0,
        bonusesActual: 0,
        walkoutMinimum: 0,
        walkoutPotential: 0,
        walkout: 0,
      } as ShowFiguresType;
    }
    return calculateShowFigures(values);
  }, [values]);

  const {
    ticketRevenuePotential,
    ticketRevenueProjected,
    ticketRevenueActual,
    venueExpenses: venueExpensesActual,
    variableRevActual,
    variableRevPotential,
    profitSharePercentPotential,
    profitSharePercentProjected,
    profitSharePercentActual,
    revenueToSplitPotential,
    revenueToSplitProjected,
    revenueToSplitActual,
    artistShareOfRevenuePotential,
    artistShareOfRevenueProjected,
    artistShareOfRevenueActual,
    walkoutPotential,
    walkoutProjected,
    walkout,
    showNetActual,
    showNetPotential,
    showBuyouts,
    bonusesPotential,
    bonusesProjected,
    bonusesActual,
    commissionsActual,
    commissionsPotential,
  } = showValues;

  const calculateDifference = (actual: number, estimated: number) => {
    return estimated ? ((actual - estimated) / estimated) * 100 : 0;
  };

  const sumAmounts = (items: { amount?: number }[]) => {
    return items.reduce((total, item) => total + (item.amount ?? 0), 0);
  };

  const showDetailsAccordions = useMemo<any>(() => {
    return () => {
      const accordions = [
        {
          headerTitle: "Revenue To Split",
          estimated: revenueToSplitProjected,
          actual: revenueToSplitActual,
          difference:
            revenueToSplitProjected && revenueToSplitActual
              ? ((revenueToSplitActual - revenueToSplitProjected) /
                  revenueToSplitProjected) *
                100
              : 0,
          calculatedData: [
            {
              subSectionTitle: "Gross Ticket Sales",
              invertColors: false,
              estimated: calculateGrossTicketProjected(values),
              actual: calculateGrossTicketActual(values),
              difference: calculateDifference(
                calculateGrossTicketActual(values),
                calculateGrossTicketProjected(values)
              ),
            },
            {
              subSectionTitle: "Adjustments",
              estimated: calculateGrossAdjustments(values as Show),
              actual: calculateGrossAdjustments(values as Show),
              difference: 0,
            },
            {
              subSectionTitle: "Deductions",
              invertColors: true,
              estimated: calculateGrossDeductions(values),
              actual: calculateGrossDeductions(values),
              difference: 0,
            },
            {
              subSectionTitle: "Total Ticket Fees",
              invertColors: true,
              estimated: calculateTicketFeeProjected(values as Show),
              actual: calculateTicketFeeActual(values as Show),
              difference: calculateTicketFeeProjected(values as Show)
                ? ((calculateTicketFeeActual(values as Show) -
                    calculateTicketFeeProjected(values as Show)) /
                    calculateTicketFeeProjected(values as Show)) *
                  100
                : 0,
            },
            {
              subSectionTitle: "Show Expenses",
              invertColors: true,
              estimated: calculateExpensesProjected(values as Show),
              actual: calculateExpensesActual(values as Show),
              difference: calculateExpensesProjected(values as Show)
                ? ((calculateExpensesActual(values as Show) -
                    calculateExpensesProjected(values as Show)) /
                    calculateExpensesProjected(values as Show)) *
                  100
                : 0,
            },
          ],
        },
        {
          headerTitle: "Walkout",
          estimated: walkoutProjected,
          actual: walkout,
          difference: ((walkout - walkoutProjected) / walkoutProjected) * 100,
          calculatedData: [
            {
              subSectionTitle: "Guarantee",
              estimated: values.guarantee ?? 0,
              actual: values.guarantee ?? 0,
              difference: 0,
            },
            {
              subSectionTitle: "Buyouts",
              estimated: sumAmounts(values.productionBuyouts),
              actual: sumAmounts(values.productionBuyouts),
              difference: 0,
            },
            {
              subSectionTitle: "Bonuses",
              estimated: bonusesProjected,
              actual: bonusesActual,
              difference: bonusesProjected
                ? ((bonusesActual - bonusesProjected) / bonusesProjected) * 100
                : 0,
            },
            {
              subSectionTitle: "Adjustments",
              estimated: calculateNetAdjustments(values as Show),
              actual: calculateNetAdjustments(values as Show),
              difference: 0,
            },
            {
              subSectionTitle: "Deductions",
              invertColors: true,
              estimated: calculateNetDeductions(values),
              actual: calculateNetDeductions(values),
              difference: 0,
            },
            {
              subSectionTitle: "Artist share of Revenue",
              estimated: artistShareOfRevenueProjected,
              actual: artistShareOfRevenueActual,
              difference: calculateDifference(
                artistShareOfRevenueActual,
                artistShareOfRevenueProjected
              ),
            },
          ],
        },
      ];
      const filterCriteria = SHOW_DETAILS_MAPPING[values.dealType];
      if (!filterCriteria) {
        return accordions;
      }
      return filterAccordionsByDealType(accordions, values.dealType);
    };
  }, [showValues, values.dealType]);

  return {
    showValues,
    showDetailsAccordions,
  };
};
